
import { useState, useEffect } from 'react';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import NavBar from '@/components/NavBar';

const KnowledgeBase = () => {
  const [aiChatPrompt, setAiChatPrompt] = useState<string>('');
  const [aiRolePlayPrompt, setAiRolePlayPrompt] = useState<string>('');
  const [callAnalyzerPrompt, setCallAnalyzerPrompt] = useState<string>('');
  const [emailSequencePrompt, setEmailSequencePrompt] = useState<string>('');
  const [personalizeOutreachPrompt, setPersonalizeOutreachPrompt] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState(true);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_prompts')
        .select('type, content');

      if (error) throw error;

      if (data) {
        data.forEach((prompt) => {
          switch (prompt.type) {
            case 'ai_chat':
              setAiChatPrompt(prompt.content);
              break;
            case 'ai_role_play':
              setAiRolePlayPrompt(prompt.content);
              break;
            case 'call_analyzer':
              setCallAnalyzerPrompt(prompt.content);
              break;
            case 'email_sequence':
              setEmailSequencePrompt(prompt.content);
              break;
            case 'personalize_outreach':
              setPersonalizeOutreachPrompt(prompt.content);
              break;
          }
        });
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
      toast.error('Failed to load prompts');
    }
  };

  const handleSave = async (type: string, value: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('ai_prompts')
        .upsert(
          { 
            type: type.toLowerCase().replace(' ', '_'),
            content: value,
            updated_at: new Date().toISOString()
          },
          { onConflict: 'type' }
        );

      if (error) throw error;
      toast.success(`${type} prompt updated successfully`);
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast.error(`Failed to save ${type} prompt`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 relative bg-gray-50">
          <NavBar onSignOut={() => {}} isAuthenticated={isAuthenticated} />
          <main className="p-8 pt-24">
            <div className="max-w-4xl mx-auto space-y-8">
              <h1 className="text-3xl font-bold">Knowledge Base</h1>
              <p className="text-gray-600">
                Customize the AI prompts used across different features of the application.
              </p>

              <div className="space-y-8">
                {/* AI Chat Prompt */}
                <Card className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="aiChatPrompt" className="text-xl font-semibold">
                        AI Chat Prompt
                      </Label>
                      <Button 
                        onClick={() => handleSave('ai_chat', aiChatPrompt)}
                        variant="outline"
                        disabled={loading}
                      >
                        Save Changes
                      </Button>
                    </div>
                    <Textarea
                      id="aiChatPrompt"
                      value={aiChatPrompt}
                      onChange={(e) => setAiChatPrompt(e.target.value)}
                      className="min-h-[300px] font-mono"
                    />
                  </div>
                </Card>

                {/* AI Role Play Prompt */}
                <Card className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="aiRolePlayPrompt" className="text-xl font-semibold">
                        AI Role Play Prompt
                      </Label>
                      <Button 
                        onClick={() => handleSave('ai_role_play', aiRolePlayPrompt)}
                        variant="outline"
                        disabled={loading}
                      >
                        Save Changes
                      </Button>
                    </div>
                    <Textarea
                      id="aiRolePlayPrompt"
                      value={aiRolePlayPrompt}
                      onChange={(e) => setAiRolePlayPrompt(e.target.value)}
                      className="min-h-[300px] font-mono"
                    />
                  </div>
                </Card>

                {/* Call Analyzer Prompt */}
                <Card className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="callAnalyzerPrompt" className="text-xl font-semibold">
                        Call Analyzer Prompt
                      </Label>
                      <Button 
                        onClick={() => handleSave('call_analyzer', callAnalyzerPrompt)}
                        variant="outline"
                        disabled={loading}
                      >
                        Save Changes
                      </Button>
                    </div>
                    <Textarea
                      id="callAnalyzerPrompt"
                      value={callAnalyzerPrompt}
                      onChange={(e) => setCallAnalyzerPrompt(e.target.value)}
                      className="min-h-[300px] font-mono"
                    />
                  </div>
                </Card>

                {/* Email Sequence Prompt */}
                <Card className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="emailSequencePrompt" className="text-xl font-semibold">
                        Email Sequence Prompt
                      </Label>
                      <Button 
                        onClick={() => handleSave('email_sequence', emailSequencePrompt)}
                        variant="outline"
                        disabled={loading}
                      >
                        Save Changes
                      </Button>
                    </div>
                    <Textarea
                      id="emailSequencePrompt"
                      value={emailSequencePrompt}
                      onChange={(e) => setEmailSequencePrompt(e.target.value)}
                      className="min-h-[300px] font-mono"
                      placeholder="Create a comprehensive prompt for generating personalized email sequences. Include instructions for researching prospects, analyzing company/industry information, determining email types (intro, value-add, case study, follow-up, closing), and defining the tone and structure for each email in the sequence."
                    />
                  </div>
                </Card>

                {/* Personalize Outreach Prompt */}
                <Card className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="personalizeOutreachPrompt" className="text-xl font-semibold">
                        Personalize Outreach Prompt
                      </Label>
                      <Button 
                        onClick={() => handleSave('personalize_outreach', personalizeOutreachPrompt)}
                        variant="outline"
                        disabled={loading}
                      >
                        Save Changes
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600">
                      This prompt guides the AI in conducting deep research on prospects and generating personalized, value-first outreach messages with real-time web search capabilities.
                    </p>
                    <Textarea
                      id="personalizeOutreachPrompt"
                      value={personalizeOutreachPrompt}
                      onChange={(e) => setPersonalizeOutreachPrompt(e.target.value)}
                      className="min-h-[300px] font-mono"
                      placeholder="Define how the AI should research prospects using web search and craft value-first personalized outreach messages..."
                    />
                  </div>
                </Card>
              </div>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default KnowledgeBase;
